import { NavLink, Outlet } from "react-router-dom";
import { useUser } from "../lib/auth";
import { api } from "../lib/api";
import { FeedbackButton } from "./FeedbackButton";

const CANONICAL_URL = "https://better-artifacts.com";
const ABOUT_URL = "https://better-artifacts.com/a/hgqnfzjg/";

/**
 * Migration banner — shown on every load of the legacy Cloud Run *.run.app URL.
 *
 * Persistent dismissal was removed: the banner must stay visible until users
 * migrate, since a one-time notice is easy to miss and forget.
 */
function MigrationBanner(): JSX.Element | null {
  if (typeof window === "undefined") return null;
  if (!/\.run\.app$/i.test(window.location.host)) return null;

  return (
    <div className="border-b border-amber-300 bg-amber-50 text-amber-900">
      <div className="mx-auto flex max-w-6xl items-center px-6 py-2 text-sm">
        <span>
          We&rsquo;ve moved to{" "}
          <a
            href={CANONICAL_URL}
            className="font-semibold underline underline-offset-2"
          >
            {CANONICAL_URL}
          </a>
          . The new <strong>better-artifacts</strong> MCP connector is already
          in Claude.ai — just authenticate it. This URL keeps working for now.
        </span>
      </div>
    </div>
  );
}

const navStyles = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium ${
    isActive
      ? "bg-slate-900 text-white"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

/** Top-level chrome: header with tab nav + user chip + sign-out. */
export function Layout() {
  const { user } = useUser();

  return (
    <div className="min-h-screen flex flex-col">
      <MigrationBanner />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2">
            <a
              href={ABOUT_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-2.5 hover:opacity-90"
              aria-label="Better Artifacts — About"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-lg font-extrabold leading-none text-ink">
                B
              </div>
              <span className="text-lg font-semibold tracking-tight text-slate-900">
                Better Artifacts
              </span>
            </a>
            <nav className="ml-8 flex items-center gap-1">
              <NavLink to="/artifacts" className={navStyles}>
                Artifacts
              </NavLink>
              <NavLink to="/integrations" className={navStyles}>
                Integrations
              </NavLink>
              <NavLink to="/feature-requests" className={navStyles}>
                Feature requests
              </NavLink>
              <a
                href={ABOUT_URL}
                target="_blank"
                rel="noreferrer noopener"
                className={navStyles({ isActive: false })}
              >
                About
              </a>
              {user?.role === "admin" && (
                <>
                  <NavLink to="/all-artifacts" className={navStyles}>
                    All artifacts
                  </NavLink>
                  <NavLink to="/admin" className={navStyles}>
                    Admin
                  </NavLink>
                </>
              )}
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-sm text-slate-600">
            <span title={user?.email}>{user?.email?.split("@")[0]}</span>
            <button
              type="button"
              onClick={async () => {
                await api.logout();
                window.location.href = "/api/auth/google/login";
              }}
              className="rounded-md border border-slate-200 px-2.5 py-1 text-xs hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">
        <Outlet />
      </main>
      <FeedbackButton />
    </div>
  );
}
